// ── routeError — centralized error-class router ───────────────────────────────
// Decides which recovery surface an error-code class belongs on, so call
// sites never branch on codes themselves. Pure, no React, structural input
// (works on a ProblemDetails, an ApiError, or a bare `{ code }`). Spec: UI
// Specs: errorzen v2.1 §UI error-class routing (FR-049, Epic ASC-6291).

import { isRetryable } from '@platform/errorzen-contracts'

export type ErrorSurface = 'reauth' | 'access-denied' | 'field-errors' | 'not-found' | 'error-state'

export interface FieldError {
  /** RFC 6901 JSON Pointer to the offending input field. */
  pointer: string
  message: string
}

/** Structural input — any error-ish value; every field is optional. */
export interface RoutableError {
  code?: string | null
  retryable?: boolean | null
  retry_after_seconds?: number | null
  retryAfter?: number | null
  details?: unknown
}

export interface RoutedError {
  surface: ErrorSurface
  retry: boolean
  /** Backoff hint in seconds for retryable errors — null when not provided. */
  retryAfter: number | null
  /** Populated for VALIDATION.* from `details.validation`; empty otherwise. */
  fieldErrors: FieldError[]
}

function readRetryAfter(error: RoutableError): number | null {
  const candidate = error.retry_after_seconds ?? error.retryAfter
  return typeof candidate === 'number' && Number.isFinite(candidate) && candidate >= 0
    ? candidate
    : null
}

function extractFieldErrors(details: unknown): FieldError[] {
  if (typeof details !== 'object' || details === null) return []
  const validation = (details as Record<string, unknown>).validation
  if (!Array.isArray(validation)) return []
  const fieldErrors: FieldError[] = []
  for (const item of validation) {
    if (typeof item !== 'object' || item === null) continue
    const { pointer, message } = item as Record<string, unknown>
    if (typeof pointer === 'string' && typeof message === 'string') {
      fieldErrors.push({ pointer, message })
    }
  }
  return fieldErrors
}

/**
 * Route an error to its recovery surface (FR-049):
 *
 * | Code class | Surface | Retry |
 * |---|---|---|
 * | `AUTH.*` | `reauth` (session recovery — reauth, not retry) | no |
 * | `ACCESS.*` | `access-denied` | no |
 * | `VALIDATION.*` | `field-errors` (attach to inputs via JSON Pointers) | no |
 * | `RESOURCE.NOT_FOUND` | `not-found` (context-specific entity state) | no |
 * | `RESOURCE.RATE_LIMITED` | `error-state` + backoff via `retryAfter` | yes |
 * | transient `UPSTREAM.*`, unknown, `INTERNAL.*`, non-problem | `error-state` | only if structurally retryable |
 *
 * Class matching is prefix-based on the code string, so an unknown member of
 * a known class (e.g. a newer backend's `AUTH.MFA_REQUIRED`) still routes to
 * the right surface.
 */
export function routeError(error: RoutableError): RoutedError {
  const code = typeof error.code === 'string' ? error.code : ''

  if (code.startsWith('AUTH.')) {
    return { surface: 'reauth', retry: false, retryAfter: null, fieldErrors: [] }
  }
  if (code.startsWith('ACCESS.')) {
    return { surface: 'access-denied', retry: false, retryAfter: null, fieldErrors: [] }
  }
  if (code.startsWith('VALIDATION.')) {
    return {
      surface: 'field-errors',
      retry: false,
      retryAfter: null,
      fieldErrors: extractFieldErrors(error.details),
    }
  }
  if (code === 'RESOURCE.NOT_FOUND') {
    return { surface: 'not-found', retry: false, retryAfter: null, fieldErrors: [] }
  }
  if (code === 'RESOURCE.RATE_LIMITED') {
    return {
      surface: 'error-state',
      retry: true,
      retryAfter: readRetryAfter(error),
      fieldErrors: [],
    }
  }

  // Transient UPSTREAM.*, unknown codes, INTERNAL.*, and non-problem Errors:
  // the generic ErrorState surface; retry only when structurally retryable.
  return {
    surface: 'error-state',
    retry: isRetryable(error),
    retryAfter: readRetryAfter(error),
    fieldErrors: [],
  }
}
